import { useEffect, useState } from "react";
import axios, { API } from "../../api";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_LABEL = {
  available: "Connect",
  awaiting_credentials: "Coming soon",
  coming_soon: "Coming soon",
};

export function IntegrationsModal({ onClose, isPremium, onUpgrade }) {
  const { user, refreshUser } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [xAttribution, setXAttribution] = useState(!!user?.x_attribution_enabled);
  const [savingPref, setSavingPref] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => { load(); }, []);

  // External trigger (post-OAuth callback) to refresh the connected state
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("aura:integrations-refresh", handler);
    return () => window.removeEventListener("aura:integrations-refresh", handler);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/integrations`);
      setPlatforms(res.data.platforms || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAction = async (p) => {
    if (!isPremium) { onUpgrade(); return; }
    setBusy(p.id);
    try {
      if (p.connected) {
        if (!confirm(`Disconnect ${p.name}?`)) { setBusy(null); return; }
        await axios.delete(`${API}/integrations/${p.id}`);
        await load();
        // Tell MainApp to re-evaluate the shimmer/+ badge on the settings button.
        try { window.dispatchEvent(new Event("aura:integrations-refresh")); } catch (_) {}
      } else {
        // Check if we can connect; otherwise show a clear message
        const info = await axios.get(`${API}/integrations/${p.id}/connect-info`);
        if (!info.data.can_connect) {
          alert(info.data.message);
          return;
        }
        if (p.id === "twitter") {
          // Real OAuth 2.0 PKCE flow for X
          const r = await axios.get(`${API}/x/connect`);
          if (r.data?.authorize_url) {
            window.location.href = r.data.authorize_url;
            return;
          }
          alert("Couldn't start X connection — please try again.");
        } else if (p.id === "snapchat") {
          // Real OAuth 2.0 PKCE flow for Snap
          const r = await axios.get(`${API}/snap/connect`);
          if (r.data?.authorize_url) {
            window.location.href = r.data.authorize_url;
            return;
          }
          alert("Couldn't start Snapchat connection — please try again.");
        } else if (p.id === "spotify") {
          // Real OAuth 2.0 PKCE flow for Spotify
          const r = await axios.get(`${API}/spotify/connect`);
          if (r.data?.authorize_url) {
            window.location.href = r.data.authorize_url;
            return;
          }
          alert("Couldn't start Spotify connection — please try again.");
        } else if (p.id === "tiktok") {
          // Phase 30.N — Real OAuth 2.0 flow for TikTok Login Kit
          const r = await axios.get(`${API}/tiktok/connect`);
          if (r.data?.authorize_url) {
            window.location.href = r.data.authorize_url;
            return;
          }
          alert("Couldn't start TikTok connection — please try again.");
        } else if (p.id === "instagram") {
          // Phase 30.U — Real OAuth 2.0 flow for Instagram Business Login
          const r = await axios.get(`${API}/instagram/connect`);
          if (r.data?.authorize_url) {
            window.location.href = r.data.authorize_url;
            return;
          }
          alert("Couldn't start Instagram connection — please try again.");
        } else {
          alert(`Coming next: real OAuth flow for ${p.name}. Backend is ready, just needs the API credentials.`);
        }
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.detail || "Couldn't reach the server. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="integrations-modal" onClick={(e) => e.stopPropagation()} data-testid="integrations-modal">
        <div className="journal-header">
          <h2>🔗 Connected Accounts {!isPremium && <span className="premium-badge-small">PRO</span>}</h2>
          <button className="journal-close" onClick={onClose} data-testid="integrations-close-btn">×</button>
        </div>
        <p className="journal-subtitle">Let Aura learn about you across the apps you use most.</p>

        {/* Manage Subscription — opens Stripe Customer Portal */}
        {isPremium && (
          <button
            type="button"
            className="manage-subscription-btn"
            data-testid="manage-subscription-btn"
            disabled={openingPortal}
            onClick={async () => {
              setOpeningPortal(true);
              try {
                const r = await axios.post(`${API}/billing/portal`);
                if (r.data?.url) {
                  window.location.href = r.data.url;
                } else {
                  alert("Couldn't open billing portal. Try again.");
                }
              } catch (e) {
                alert(e?.response?.data?.detail || "Couldn't open billing portal.");
              } finally {
                setOpeningPortal(false);
              }
            }}
          >
            {openingPortal ? "Opening…" : "💳 Manage Subscription"}
          </button>
        )}

        {!isPremium && (
          <div className="theme-locked-banner">
            <span>💜 Social integrations are a Premium feature — $1.99/mo</span>
          </div>
        )}

        {loading ? (
          <div className="journal-loading">Loading...</div>
        ) : (
          <div className="integrations-list">
            {platforms.map((p) => {
              const isComingSoon = p.status === "coming_soon" || p.status === "awaiting_credentials";
              const statusText = p.connected
                ? `Connected${p.display_name ? ` as ${p.display_name}` : ""}`
                : (p.status === "available" ? "Ready to connect" : (p.status === "awaiting_credentials" ? "Awaiting setup" : "Coming soon"));

              return (
                <div
                  key={p.id}
                  className={`integration-row ${isComingSoon ? "soon" : ""} ${p.connected ? "connected" : ""}`}
                  data-testid={`integration-${p.id}`}
                >
                  <div className="integration-icon" style={{ background: p.color }}>
                    <span>{p.emoji}</span>
                  </div>
                  <div className="integration-info">
                    <div className="integration-name-row">
                      <strong>{p.name}</strong>
                      {isComingSoon && <span className="soon-pill">Coming soon</span>}
                      {p.connected && <span className="connected-pill">✓ Connected</span>}
                    </div>
                    <span className="integration-tagline">{p.tagline}</span>
                    <span className="integration-status">{statusText}</span>
                    {/* X attribution toggle — only when X is connected. Lets users
                        opt in to auto-appending an Aura attribution to tweets she
                        posts on their behalf. Off by default. */}
                    {p.id === "twitter" && p.connected && (
                      <label className="integration-toggle" data-testid="x-attribution-toggle-row">
                        <input
                          type="checkbox"
                          checked={xAttribution}
                          disabled={savingPref}
                          data-testid="x-attribution-toggle"
                          onChange={async (e) => {
                            const next = e.target.checked;
                            setXAttribution(next);
                            setSavingPref(true);
                            try {
                              await axios.post(`${API}/user/preferences`, {
                                x_attribution_enabled: next,
                              });
                              await refreshUser?.();
                            } catch (_) {
                              setXAttribution(!next); // revert on failure
                            } finally {
                              setSavingPref(false);
                            }
                          }}
                        />
                        <span>Add <code>✦ aura.wwwwhynot.com</code> to my tweets</span>
                      </label>
                    )}
                  </div>
                  <button
                    className={`integration-action ${p.connected ? "disconnect" : ""}`}
                    onClick={() => handleAction(p)}
                    disabled={busy === p.id || (!isPremium && !isComingSoon)}
                    data-testid={`integration-action-${p.id}`}
                  >
                    {busy === p.id
                      ? "..."
                      : p.connected
                        ? "Disconnect"
                        : STATUS_LABEL[p.status] || "Connect"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="integrations-footnote">
          We never post on your behalf without asking. You can disconnect anytime.
        </p>
      </div>
    </div>
  );
}
